import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { searchProperties, getCities, getAmenities } from '../lib/api';
import { Filter, Loader2, ArrowRight, X } from 'lucide-react';

const PROPERTY_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'villa', label: 'Villa' },
  { value: 'cottage', label: 'Cottage' },
  { value: 'apartment', label: 'Apartment' },
];

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  const [cities, setCities] = useState([]);
  const [amenities, setAmenities] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    check_in: searchParams.get('check_in') || '',
    check_out: searchParams.get('check_out') || '',
    guests: searchParams.get('guests') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    beds: searchParams.get('beds') || '',
    baths: searchParams.get('baths') || '',
    property_type: searchParams.get('property_type') || '',
    amenities: searchParams.get('amenities')?.split(',').filter(Boolean) || [],
  });

  // Load cities and amenities
  useEffect(() => {
    const loadData = async () => {
      try {
        const [citiesRes, amenitiesRes] = await Promise.all([
          getCities(),
          getAmenities()
        ]);
        setCities(citiesRes.data);
        setAmenities(amenitiesRes.data);
      } catch (error) {
        console.error('Failed to load filter data:', error);
      }
    };
    loadData();
  }, []);

  // Search properties
  const search = useCallback(async (cursor = null) => {
    setLoading(true);
    try {
      const params = {
        limit: 20,
        cursor
      };
      
      if (filters.city && filters.city !== 'all') params.city = filters.city;
      if (filters.check_in) params.check_in = filters.check_in;
      if (filters.check_out) params.check_out = filters.check_out;
      if (filters.guests) params.guests = filters.guests;
      if (filters.min_price) params.min_price = parseInt(filters.min_price) * 100;
      if (filters.max_price) params.max_price = parseInt(filters.max_price) * 100;
      if (filters.beds && filters.beds !== 'any') params.beds = filters.beds;
      if (filters.baths && filters.baths !== 'any') params.baths = filters.baths;
      if (filters.property_type && filters.property_type !== 'all') params.property_type = filters.property_type;
      if (filters.amenities.length > 0) params.amenities = filters.amenities.join(',');
      
      const response = await searchProperties(params);
      
      if (cursor) {
        setProperties(prev => [...prev, ...response.data.items]);
      } else {
        setProperties(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial search
  useEffect(() => {
    search();
  }, []);

  const handleSearch = () => {
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    });
    setSearchParams(params);
    search();
  };

  const handleLoadMore = () => {
    if (nextCursor) {
      search(nextCursor);
    }
  };

  const handleAmenityToggle = (amenityId) => {
    setFilters(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      check_in: '',
      check_out: '',
      guests: '',
      min_price: '',
      max_price: '',
      beds: '',
      baths: '',
      property_type: '',
      amenities: [],
    });
    setSearchParams(new URLSearchParams());
    search();
  };

  const activeFiltersCount = Object.values(filters).filter(v => 
    Array.isArray(v) ? v.length > 0 : v
  ).length;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* City */}
      <div className="space-y-2">
        <Label>Destination</Label>
        <Select 
          value={filters.city} 
          onValueChange={(value) => setFilters(p => ({ ...p, city: value }))}
        >
          <SelectTrigger data-testid="filter-city">
            <SelectValue placeholder="All cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city.id} value={city.id}>
                {city.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div className="space-y-2">
        <Label>Price Range (per night)</Label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            type="number"
            placeholder="Min $"
            value={filters.min_price}
            onChange={(e) => setFilters(p => ({ ...p, min_price: e.target.value }))}
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder="Max $"
            value={filters.max_price}
            onChange={(e) => setFilters(p => ({ ...p, max_price: e.target.value }))}
            data-testid="filter-max-price"
          />
        </div>
      </div>

      {/* Beds & Baths */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Beds</Label>
          <Select 
            value={filters.beds} 
            onValueChange={(value) => setFilters(p => ({ ...p, beds: value }))}
          >
            <SelectTrigger data-testid="filter-beds">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Baths</Label>
          <Select 
            value={filters.baths} 
            onValueChange={(value) => setFilters(p => ({ ...p, baths: value }))}
          >
            <SelectTrigger data-testid="filter-baths">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              {[1, 2, 3, 4, 5].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Property Type */}
      <div className="space-y-2">
        <Label>Property Type</Label>
        <Select 
          value={filters.property_type} 
          onValueChange={(value) => setFilters(p => ({ ...p, property_type: value }))}
        >
          <SelectTrigger data-testid="filter-property-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PROPERTY_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Amenities */}
      <div className="space-y-3">
        <Label>Amenities</Label>
        <div className="grid grid-cols-2 gap-3">
          {amenities.slice(0, 6).map((amenity) => (
            <div key={amenity.id} className="flex items-center space-x-2">
              <Checkbox
                id={amenity.id}
                checked={filters.amenities.includes(amenity.id)}
                onCheckedChange={() => handleAmenityToggle(amenity.id)}
                data-testid={`filter-amenity-${amenity.id}`}
              />
              <label htmlFor={amenity.id} className="text-sm cursor-pointer">
                {amenity.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={clearFilters}
        >
          Clear All
        </Button>
        <Button 
          className="flex-1 btn-primary"
          onClick={handleSearch}
          data-testid="apply-filters-btn"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );

  return (
    <PublicLayout>
      <SEO
        title="Search Results"
        description="Find your perfect vacation rental in Southwest Florida. Browse properties in Cape Coral, Fort Myers, Sanibel, Naples and more."
        url="/search"
      />
      <div className="container-main section-padding">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-semibold">Filters</h2>
                {activeFiltersCount > 0 && (
                  <button 
                    onClick={clearFilters}
                    className="text-sm text-primary hover:underline"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Header with Mobile Filter Button */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">
                  {filters.city 
                    ? cities.find(c => c.id === filters.city)?.name || 'Search Results'
                    : 'All Properties'
                  }
                </h1>
                <p className="text-slate-500 mt-1">
                  {loading ? 'Searching...' : hasMore ? '20+ results' : `${properties.length} results`}
                </p>
              </div>

              {/* Mobile Filter Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <span className="ml-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-full sm:max-w-md">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Results */}
            {loading && properties.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : properties.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} showSaveButton />
                  ))}
                </div>

                {hasMore && (
                  <div className="mt-12 text-center">
                    <Button 
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="btn-outline"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      Load More
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 bg-slate-50 rounded-xl">
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  No properties found
                </h3>
                <p className="text-slate-500 mb-6">
                  Try adjusting your filters or search criteria.
                </p>
                <Button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
